import { useEffect, useRef, useState, type FC } from 'react'
import toast from 'react-hot-toast'
import type { GridEditorProps } from '../types'
import classes from '../grid-theme.module.scss'

/**
 * Numeric cell editor. Commits a number, or `undefined` when the field is left
 * blank — the ET reference grid distinguishes "not set yet" from a stored zero.
 *
 * `min`/`max` clamp the committed value. A column that must not quietly change
 * what was typed passes `outOfRangeMessage` as well: the input turns red while
 * the value is outside the range, Enter is refused with that message, and
 * leaving the cell discards the value instead of clamping it.
 *
 * `params.clamp: false` hands an out-of-range or unreadable value to the grid
 * instead of fixing it quietly: a value outside `min`/`max` is committed as
 * typed, and text the browser could not read as a number is committed as NaN.
 * The grid then refuses it and says why. By default the editor still clamps
 * and drops unreadable text, as every other grid expects.
 */
const NumberEditor: FC<GridEditorProps<unknown>> = ({
  value,
  params,
  onCommit,
  onCancel,
}) => {
  const [draft, setDraft] = useState(value == null ? '' : String(value))
  const inputRef = useRef<HTMLInputElement>(null)
  const cancelledRef = useRef(false)
  // Whichever path commits first wins; the rest become no-ops, so a blur
  // followed by unmount cannot commit the same edit twice.
  const settledRef = useRef(false)
  const commitRef = useRef<() => void>(() => {})

  useEffect(() => {
    inputRef.current?.focus()
    inputRef.current?.select()

    // Teardown commits: a control that swallows `mousedown` to protect the row
    // underneath it never lets the input blur, so unmounting the editor would
    // otherwise discard the typed value.
    return () => {
      if (settledRef.current || cancelledRef.current) return
      commitRef.current()
    }
  }, [])

  const min = typeof params?.min === 'number' ? params.min : undefined
  const max = typeof params?.max === 'number' ? params.max : undefined
  const outOfRangeMessage =
    typeof params?.outOfRangeMessage === 'string'
      ? params.outOfRangeMessage
      : undefined

  const isOutOfRange = (text: string) => {
    if (!outOfRangeMessage || text.trim() === '') return false
    const parsed = Number(text)
    if (Number.isNaN(parsed)) return false
    return (min != null && parsed < min) || (max != null && parsed > max)
  }
  const invalid = isOutOfRange(draft)

  // One toast per message, so pressing Enter again does not stack copies.
  const rejectOutOfRange = () => {
    toast.error(outOfRangeMessage!, { id: outOfRangeMessage })
  }
  const clamp = params?.clamp !== false
  // A number input reports '' for text it cannot read ("abc", "1e"), the same
  // as an empty field; only `validity.badInput` tells the two apart.
  const badInputRef = useRef(false)

  const commit = () => {
    if (settledRef.current || cancelledRef.current) return
    if (isOutOfRange(draft)) {
      // Leaving the cell: keep the value it had rather than a clamped one the
      // user never typed.
      settledRef.current = true
      rejectOutOfRange()
      onCancel()
      return
    }
    settledRef.current = true
    const trimmed = draft.trim()
    if (trimmed === '') {
      if (!clamp && badInputRef.current) {
        onCommit(Number.NaN)
        return
      }
      onCommit(undefined)
      return
    }
    const parsed = Number(trimmed)
    if (Number.isNaN(parsed)) {
      if (!clamp) {
        onCommit(Number.NaN)
        return
      }
      onCancel()
      return
    }
    let next = parsed
    if (clamp && min != null) next = Math.max(next, min)
    if (clamp && max != null) next = Math.min(next, max)
    onCommit(next)
  }

  commitRef.current = commit

  return (
    <input
      ref={inputRef}
      type="number"
      className={`${classes.editorInput} ${classes.editorNumeric}${
        invalid ? ` ${classes.editorInvalid}` : ''
      }`}
      value={draft}
      min={min}
      max={max}
      aria-invalid={invalid || undefined}
      onChange={(e) => {
        badInputRef.current = Boolean(e.target.validity?.badInput)
        setDraft(e.target.value)
      }}
      onBlur={() => {
        if (cancelledRef.current) return
        commit()
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          // Stay in the cell so the value can be corrected. The grid moves to
          // the row below on Enter, so the key must not reach it.
          if (invalid) {
            e.stopPropagation()
            rejectOutOfRange()
            return
          }
          commit()
        } else if (e.key === 'Escape') {
          e.preventDefault()
          cancelledRef.current = true
          onCancel()
        } else if (e.key === 'Tab') {
          commit()
        }
      }}
    />
  )
}

export default NumberEditor
